import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { StreamRefusal } from '../lib/appendLogStream';
import type { RunEventRow } from '../lib/fleetStream';
import type { StreamStatus } from '../lib/sseFrames';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { FleetRunTimeline } from './FleetRunTimeline';
import { Panel } from './Panel';
import { RunIdBadge } from './RunIdBadge';
import { isAnsweredRefusal, RunStreamRefusal } from './RunStreamRefusal';
import { Skeleton } from './Skeleton';

// The run's body, split out of the route so <FleetAudit/> stays the container.
//
// FOUR states, in the order they must be checked, because each earlier one is a
// stronger claim than the next:
//
//   1. the stream refused with an ANSWER (404, or no session) →
//      <RunStreamRefusal/>, and no Retry: it would 404 for ever;
//   2. the stream broke, or the server faulted (503/500/429) → <ErrorState/>,
//      which keeps its Retry, because those ARE worth retrying;
//   3. still connecting → skeletons;
//   4. landed → the timeline, or the empty state.
//
// "No events for this run yet." is the weakest claim and therefore last: prod
// has ZERO runs with actually-zero events, so anything reaching it is either a
// brand-new run or a bug, and it must never stand in for a refusal.

export type RunTimelinePanelProps = {
  readonly runId: string;
  readonly events: readonly RunEventRow[];
  readonly status: StreamStatus;
  readonly refusal: StreamRefusal | null;
  readonly onRetry: () => void;
  /** Injectable for tests; the route passes its own `props.token`. */
  readonly token: string | null;
};

export function RunTimelinePanel(props: RunTimelinePanelProps): JSX.Element {
  return (
    <Panel title={t('fleet.events')} action={<RunIdBadge runId={props.runId} />}>
      <Show
        when={props.refusal !== null && isAnsweredRefusal(props.refusal) ? props.refusal : null}
        fallback={
          <Show
            when={props.status !== 'error'}
            fallback={<ErrorState message={t('fleet.status_error')} onRetry={props.onRetry} />}
          >
            <Show
              when={props.events.length > 0}
              fallback={
                <Show
                  when={props.status === 'connecting'}
                  fallback={<EmptyState message={t('fleet.audit_empty')} />}
                >
                  <div
                    class="fleet-timeline__loading"
                    role="status"
                    aria-label={t('states.loading')}
                  >
                    <Skeleton height="4.5rem" />
                    <Skeleton height="4.5rem" />
                    <Skeleton height="4.5rem" />
                  </div>
                </Show>
              }
            >
              <FleetRunTimeline events={props.events} />
            </Show>
          </Show>
        }
      >
        {(refusal) => (
          <RunStreamRefusal refusal={refusal()} runId={props.runId} token={props.token} />
        )}
      </Show>
    </Panel>
  );
}
